import * as THREE from 'three';

export interface MatteOptions {
  color: number;
  roughness?: number;
  emissive?: number;
  emissiveIntensity?: number;
  flatShading?: boolean;
}

export function createMatteMaterial(
  options: MatteOptions
): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: options.color,
    roughness: options.roughness ?? 0.85,
    metalness: 0,
    emissive: options.emissive ?? 0x000000,
    emissiveIntensity: options.emissiveIntensity ?? 0,
    flatShading: options.flatShading ?? false
  });
}

export function createGlowMaterial(
  color: number,
  opacity = 1.0,
  toneMapped = false
): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: opacity < 1,
    opacity,
    toneMapped
  });
}

export function createHaloMaterial(
  color: number,
  opacity = 0.14
): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    side: THREE.BackSide,
    depthWrite: false,
    toneMapped: false
  });
}

export interface AtmosphereOptions {
  readonly color: number;
  readonly intensity?: number;
  readonly power?: number;
  readonly sunColor?: number;
  readonly sunBoost?: number;
}

const atmosphereVertex = /* glsl */ `
  varying vec3 vNormalWS;
  varying vec3 vWorldPos;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPos = worldPos.xyz;
    vNormalWS = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

const atmosphereFragment = /* glsl */ `
  uniform vec3 uColor;
  uniform vec3 uSunColor;
  uniform float uIntensity;
  uniform float uPower;
  uniform float uSunBoost;
  varying vec3 vNormalWS;
  varying vec3 vWorldPos;

  void main() {
    vec3 N = normalize(vNormalWS);
    vec3 viewDir = normalize(cameraPosition - vWorldPos);
    // Sun is at the system origin.
    vec3 sunDir = normalize(-vWorldPos);

    float fresnel = 1.0 - max(dot(N, viewDir), 0.0);
    fresnel = pow(fresnel, uPower);

    float litSide = max(dot(N, sunDir), 0.0);
    litSide = pow(litSide, 0.55);

    vec3 col = mix(uColor, uSunColor, litSide * uSunBoost);
    float intensity = fresnel * (0.22 + litSide * 0.95) * uIntensity;

    gl_FragColor = vec4(col, intensity);
  }
`;

/**
 * Atmosphere shell for planets/moons — Fresnel rim glow that brightens on
 * the sun-facing side and fades to dark on the night side. Use on a sphere
 * slightly larger than the body, with BackSide so it reads as a halo around
 * the silhouette without occluding the body itself.
 */
export function createAtmosphereMaterial(
  options: AtmosphereOptions
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(options.color) },
      uSunColor: {
        value: new THREE.Color(options.sunColor ?? 0xfff1d6)
      },
      uIntensity: { value: options.intensity ?? 1.0 },
      uPower: { value: options.power ?? 3.0 },
      uSunBoost: { value: options.sunBoost ?? 0.4 }
    },
    vertexShader: atmosphereVertex,
    fragmentShader: atmosphereFragment,
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    toneMapped: true
  });
}
